import { useLayoutEffect } from 'react'
import { Provider } from 'react-redux'
import { RouterProvider, type createBrowserRouter } from 'react-router-dom'

import { WindowShellContext, type WindowShell } from '@krp/client-runtime'
import { NotificationProvider } from '@krp/ui-kit'

import { ActionInfoBridge } from './ActionHints'
import { ConnectionMonitor } from './ConnectionMonitor'
import { ConnectionOverlay } from './ConnectionOverlay'
import { MaintenanceMonitor } from './MaintenanceMonitor'
import { ShellHealthRoot } from './ShellHealth'
import { Notifications } from './Notifications'
import { useAppSelector } from './hooks'
import type { AppStore } from './store'
import { applyTheme, resolveTheme, selectTheme } from './theme'
import { UpdateBanner } from '../update/UpdateBanner'

type AppRouter = ReturnType<typeof createBrowserRouter>


/** Корень React: один store, одна оболочка окон, подсказки ⓘ из реестра действий, один маршрутизатор. */
export function App({
  store,
  shell,
  router,
  browserFetch,
}: {
  store: AppStore
  shell: WindowShell
  router: AppRouter
  /** Браузерный fetch composition root — для кнопки «Обновить» плашки обновления (перечитать документ мимо кэша); в тестах нет. */
  browserFetch?: typeof fetch
}) {
  return (
    <Provider store={store}>
      <ThemeRoot />
      <WindowShellContext.Provider value={shell}>
        <NotificationProvider>
          <ActionInfoBridge>
            {browserFetch !== undefined && <UpdateBanner fetchImpl={browserFetch} />}
            {/* Связь окна: доска сигналов и срез связи для экранов; общее окно связи читает её же (экран продажи показывает связь сам). */}
            <ShellHealthRoot>
              <RouterProvider router={router} />
              <Notifications />
              {/* Связь с сервером — одна на всё приложение: проверки доступности и окно по центру при обрыве. */}
              <ConnectionMonitor />
              <ConnectionOverlay />
              {/* RP-MNT: подписка на режим обновления РМК (пока активна сессия). */}
              <MaintenanceMonitor />
            </ShellHealthRoot>
          </ActionInfoBridge>
        </NotificationProvider>
      </WindowShellContext.Provider>
    </Provider>
  )
}

/**
 * Тема на `<html>` — выше входа и любого окна: экран «Проверяем вход…» уже в
 * выбранной теме. Layout-эффект — до отрисовки, без вспышки системной темы.
 */
function ThemeRoot() {
  const choice = useAppSelector(selectTheme)
  useLayoutEffect(() => applyTheme(document.documentElement, resolveTheme(choice)), [choice])
  return null
}
